import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, Download, IdCard, PackagePlus, FileText, Eye, Pencil, Ban, KeyRound, MoreVertical , ArrowLeftRight} from "lucide-react";
import { Users, TrendingUp, Target } from "lucide-react";
import { PageHeader } from "@/components/PageHeader";
import { BreadcrumbItem } from "@/components/Breadcrumb";
import { Panigation } from "@/components/ui/Panigation";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import Spinner from "@/components/Spinner";
import { StatsCard } from "@/components/Dashboard/StatsCard";
import { fetchPackageSummary, fetchUserPackages } from "@/api/Admin/userPackage";
import dayjs from "dayjs";
import { message } from "antd";
import * as XLSX from "xlsx";

type Investor = {
  id: string;
  code: string;
  name: string;
  avatar?: string;
  email: string;
  phone: string;
  identifier: string;
  segment: "VIP" | "LOYAL" | "REGULAR" | "NEW"; // phân loại theo backend sau
  totalSpend: number;
  totalOrders: number;
  joinedDate: string;
  status?: "active" | "inactive";
};

const mapInvestorRows = (items: any[]): Investor[] => {
  return items.map((item: any) => ({
    id: item.user_id.toString(),
    code: item.user_info.id.toString() || "",
    name: item.user_info.fullname || "",
    email: item.user_info.email || "",
    phone: item.user_info.phone || "",
    identifier: item.user_info.cccd_number || "",
    segment: "REGULAR",
    totalSpend: Number(item.total_invested) || 0,
    totalOrders: item.total_packages || 0,
    joinedDate: item.user_info.created_at || "",
    status: item.user_info.is_active === 1 ? "active" : "inactive",
  }));
};

interface ActionDropdownProps {
  onView: () => void;
  onEdit: () => void;
  onToggleStatus: () => void;
  onTransfer: () => void;
}

const ActionDropdown: React.FC<ActionDropdownProps> = ({ onView, onEdit, onToggleStatus, onTransfer }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const actions = [
    { label: t("investors.actionView"), onClick: onView },
    { label: t("investors.actionEdit"), onClick: onEdit },
    { label: t("investors.actionTransfer"), onClick: onTransfer },
  ];

  return (
    <div ref={ref} className="relative inline-block text-left">
      <button
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium focus:outline-none focus:ring-2 focus:ring-primary/50"
      >
        <MoreVertical className="w-4 h-4" />
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-1 w-48 origin-top-right rounded-md shadow-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 py-1 flex flex-col">
          {actions.map((action) => (
            <button
              key={action.label}
              onClick={() => { setOpen(false); action.onClick(); }}
              className="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
            >
              {action.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const InvestorManagement: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("");
  const [totalPages, setTotalPages] = useState(1);
  const { t } = useTranslation();

  const [summaryData, setSummaryData] = useState({
    totalUser: {
      total: 0,
      newThisMonth: 0,
    },
    totalInvestors: {
      total: 0,
      percentageOfTotal: 0,
    },
    totalInvestment: {
      value: 0,
      vsLastMonth: 0,
    },
    totalPay: {
      value: 0,
      vsLastQuarter: 0,
    },
  });
  const [investorData, setInvestorData] = useState<Investor[]>([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const itemsPerPage = 10;

  const  fetchSummary = async () => {
    setLoading(true);
    try {
      const res = await fetchPackageSummary();
      if (res?.data) {
        setSummaryData({
          totalUser: {
            total: res.data.total_investors.value,
            newThisMonth: res.data.total_investors.trend_percentage,
          },
          totalInvestors: {
            total: res.data.total_active_investors.value,
            percentageOfTotal: res.data.total_active_investors.trend_percentage,
          },
          totalInvestment: {
            value: res.data.total_capital_raised.value,
            vsLastMonth: res.data.total_capital_raised.trend_percentage,
          },
          totalPay: {
            value: res.data.total_interest_paid.value,
            vsLastQuarter: res.data.total_interest_paid.trend_percentage,
          },
        });
      }
    } catch (error) {
      console.error("Error fetching summary:", error);
    } finally {
      setLoading(false);
    }
  };


  const fetchData = async () => {
    setLoading(true);
    try {
      const res = await fetchUserPackages( currentPage, statusFilter, searchTerm);
      if (res?.data?.data) {
        setInvestorData(mapInvestorRows(res.data.data));
        setTotalPages(res.data.last_page);
      }
    } catch (error) {
      console.error("Error fetching investors:", error);
    } finally {
      setLoading(false);
    }
  };

  // Mock data
  useEffect(() => {
    fetchData();
  }, [currentPage, searchTerm, statusFilter]);

  useEffect(() => {
    setTimeout(() => {
      setCurrentPage(1);
    }, 1500);
  }, [searchTerm, statusFilter]);
    

  useEffect(() => {
    fetchSummary();
  }, []);

  const navigate = useNavigate();

  const openInvestorDetails = (investorId: string) => {
    navigate(`/admin/investors/${investorId}`);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const firstPage = await fetchUserPackages(1, statusFilter, searchTerm || undefined);
      const firstPageData = firstPage?.data;
      const lastPage = firstPageData?.last_page || 1;
      const allInvestors = mapInvestorRows(firstPageData?.data || []);

      if (lastPage > 1) {
        const pageRequests = [];
        for (let pageIndex = 2; pageIndex <= lastPage; pageIndex += 1) {
          pageRequests.push(fetchUserPackages(pageIndex, statusFilter, searchTerm || undefined));
        }

        const pageResults = await Promise.all(pageRequests);
        pageResults.forEach((result) => {
          allInvestors.push(...mapInvestorRows(result?.data?.data || []));
        });
      }

      if (allInvestors.length === 0) {
        message.info("Không có dữ liệu để xuất Excel");
        return;
      }

      const exportRows = allInvestors.map((investor, index) => ({
        STT: index + 1,
        MA_NHA_DAU_TU: investor.code,
        TEN_NHA_DAU_TU: investor.name,
        EMAIL: investor.email,
        SO_DIEN_THOAI: investor.phone,
        CCCD: investor.identifier,
        NGAY_THAM_GIA: investor.joinedDate ? dayjs(investor.joinedDate).format("DD/MM/YYYY") : "",
        TONG_DAU_TU: investor.totalSpend,
        SO_GOI: investor.totalOrders,
        TRANG_THAI: investor.status === "active" ? t("investors.active") : t("investors.inactive"),
      }));

      const columns = [
        "STT",
        "MA_NHA_DAU_TU",
        "TEN_NHA_DAU_TU",
        "EMAIL",
        "SO_DIEN_THOAI",
        "CCCD",
        "NGAY_THAM_GIA",
        "TONG_DAU_TU",
        "SO_GOI",
        "TRANG_THAI",
      ] as const;

      const headerMap: Record<(typeof columns)[number], string> = {
        STT: "STT",
        MA_NHA_DAU_TU: "Mã nhà đầu tư",
        TEN_NHA_DAU_TU: "Tên nhà đầu tư",
        EMAIL: "Email",
        SO_DIEN_THOAI: "Số điện thoại",
        CCCD: "CCCD",
        NGAY_THAM_GIA: "Ngày tham gia",
        TONG_DAU_TU: "Tổng đầu tư",
        SO_GOI: "Số gói",
        TRANG_THAI: "Trạng thái",
      };

      const worksheet = XLSX.utils.json_to_sheet(exportRows, { header: columns as unknown as string[] });
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Investors");
      XLSX.utils.sheet_add_aoa(worksheet, [columns.map((column) => headerMap[column])], { origin: "A1" });

      const now = new Date();
      const fileName = `investors_${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}_${String(now.getHours()).padStart(2, "0")}${String(now.getMinutes()).padStart(2, "0")}.xlsx`;
      XLSX.writeFile(workbook, fileName);

      message.success(`Xuất Excel thành công (${allInvestors.length} nhà đầu tư)`);
    } catch (error) {
      console.error("Export investors error:", error);
      message.error("Xuất Excel thất bại, vui lòng thử lại");
    } finally {
      setExporting(false);
    }
  };

  // const getSegmentBadge = (segment: Investor["segment"]) => {
  //   const badges = {
  //     VIP: "bg-yellow-500 text-white dark:bg-yellow-900/30 dark:text-yellow-400",
  //     LOYAL: "bg-blue-500 text-white dark:bg-blue-900/30 dark:text-blue-400",
  //     REGULAR:
  //       "bg-green-500 text-white dark:bg-green-900/30 dark:text-green-400",
  //     NEW: "bg-cyan-500 text-white dark:bg-cyan-900/30 dark:text-cyan-400",
  //   };
  //   const labels = {
  //     VIP: "VIP",
  //     LOYAL: t("investors.loyal"),
  //     REGULAR: t("investors.regular"),
  //     NEW: t("investors.new"),
  //   };
  //   return (
  //     <span
  //       className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${badges[segment]}`}
  //     >
  //       {labels[segment]}
  //     </span>
  //   );
  // };

  const getStatusBadge = (status: Investor["status"]) => {
    if (status === "active") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-green-500 text-white dark:bg-green-900/30 dark:text-green-400">
          {t("investors.active")}
        </span>
      );
    } else if (status === "inactive") {
      return (
        <span className="inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium bg-red-500 text-white dark:bg-red-900/30 dark:text-red-400">
          {t("investors.inactive")}
        </span>
      );
    }
    return null;
  }

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Dashboard", path: "/admin/dashboard" },
    { label: t("investors.title") },
  ];

  // Stats configuration
  const statsConfig = [
    {
      title: t("investors.totalInvestors"),
      value: summaryData.totalUser.total.toLocaleString('vi-VN'),
      change: summaryData.totalUser.newThisMonth.toLocaleString('vi-VN') + "%",
      changeLabel: t("investors.newThisMonth"),
      trend: "up" as const,
      icon: Users,
      bgColor: "bg-blue-500/10 dark:bg-blue-500/20",
      iconColor: "text-blue-600 dark:text-blue-400",
      chartData: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      chartColor: "#3b82f6",
      gradientColor: "#3b82f6",
    },
    {
      title: t("investors.vipInvestors"),
      value: summaryData.totalInvestors.total.toLocaleString('vi-VN'),
      change: summaryData.totalInvestors.percentageOfTotal.toLocaleString('vi-VN') + "%",
      changeLabel: t("investors.ofTotal"),
      trend: "neutral" as const,
      icon: TrendingUp,
      bgColor: "bg-yellow-500/10 dark:bg-yellow-500/20",
      iconColor: "text-yellow-600 dark:text-yellow-400",
      chartData: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      chartColor: "#eab308",
      gradientColor: "#eab308",
    },
    {
      title: t("investors.avgValue"),
      value: summaryData.totalInvestment.value.toLocaleString('vi-VN') + " đ",
      change: summaryData.totalInvestment.vsLastMonth.toLocaleString('vi-VN') + "%",
      changeLabel: t("investors.vsLastMonth"),
      trend: "up" as const,
      icon: PackagePlus,
      bgColor: "bg-green-500/10 dark:bg-green-500/20",
      iconColor: "text-green-600 dark:text-green-400",
      chartData: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      chartColor: "#22c55e",
      gradientColor: "#22c55e",
    },
    {
      title: t("investors.retentionRate"),
      value: summaryData.totalPay.value.toLocaleString('vi-VN') + " đ",
      change: summaryData.totalPay.vsLastQuarter.toLocaleString('vi-VN') + " đ",
      changeLabel: t("investors.vsLastQuarter"),
      trend: "up" as const,
      icon: Target,
      bgColor: "bg-purple-500/10 dark:bg-purple-500/20",
      iconColor: "text-purple-600 dark:text-purple-400",
      chartData: [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
      chartColor: "#a855f7",
      gradientColor: "#a855f7",
    },
  ];

  return (
    <div className="dark:bg-dark-bg">
      <Helmet>
        <title>{t("investors.title")} - Admin Dashboard</title>
      </Helmet>

      <PageHeader
        title={t("investors.title")}
        description={t("investors.subtitle")}
        breadcrumbs={breadcrumbs}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        {statsConfig.map((stat, index) => (
          <StatsCard // backend trả về trường nào thì qua StatsCard hard code trường còn lại
            key={index}
            index={index}
            title={stat.title}
            value={stat.value}
            change={stat.change}
            changeLabel={stat.changeLabel}
            trend={stat.trend}
            icon={stat.icon}
            bgColor={stat.bgColor}
            iconColor={stat.iconColor}
            chartData={stat.chartData}
            chartColor={stat.chartColor}
            gradientColor={stat.gradientColor}
          />
        ))}
      </div>

      {/* Search and Filters */}
      <div className="flex md:items-center items-start md:flex-row flex-col gap-3 mb-6">
        <div className="relative flex-1 w-full md:w-auto">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder={t("investors.searchPlaceholder")}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2.5 w-full border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        </div>
        <div className="flex justify-between items-center gap-2">
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="pl-4 pr-8 py-2.5 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          >
            <option value="">{t("investors.allStatuses")}</option>
            <option value="active">{t("investors.active")}</option>
            <option value="inactive">{t("investors.inactive")}</option>
          </select>
        </div>
        <button
          onClick={() => navigate('/admin/plans/create-contract')}
          className="flex items-center justify-center px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-md transition-colors font-medium text-sm shadow-sm"
        >
          <FileText className="h-4 w-4 mr-2" />
          {t('plan.createContract')}
        </button>
        <button
          onClick={handleExport}
          disabled={exporting}
          className="inline-flex items-center gap-2 px-4 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          <Download className="w-4 h-4" />
          {exporting ? "Đang xuất..." : t("investors.export")}
        </button>
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm mb-6">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-50 dark:bg-gray-700/50">
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("investors.code")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("investors.investor")}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("investors.identifier")}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("investors.joinDate")}
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("investors.totalSpend")}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("investors.orders")}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("investors.status")}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t("investors.actions")}
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr>
                  <td colSpan={8} className="px-6 py-4 text-center">
                    <span className="flex items-center justify-center gap-2">
                      <Spinner size={32} /> {t("common.loading")}...
                    </span>
                  </td>
                </tr>
              ) : investorData.length === 0 ? (
                <tr>
                  <td
                    colSpan={8}
                    className="px-6 py-4 text-center text-sm text-gray-700 dark:text-gray-300"
                  >
                    {t("investors.noData")}
                  </td>
                </tr>
              ) : (
                investorData.map((investor) => (
                  <tr
                    key={investor.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                      {investor.code}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 font-medium">
                          {getInitials(investor.name)}
                        </div>
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          {investor.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-700 dark:text-gray-300">
                        <div className="flex items-center gap-1 mb-1">
                          <IdCard className="w-3 h-3 text-gray-500 dark:text-gray-400" />
                          <span>{investor.identifier}</span>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-700 dark:text-gray-300">
                      {dayjs(investor.joinedDate).format("DD/MM/YYYY")}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900 dark:text-white">
                      {(investor.totalSpend).toLocaleString('vi-VN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}{" "} VNĐ
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center text-sm text-gray-700 dark:text-gray-300">
                      {investor.totalOrders}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {getStatusBadge(investor.status)}
                    </td>

                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      <button
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary hover:text-white dark:text-primary-dark hover:bg-primary dark:hover:bg-primary-dark/20 rounded-md transition-colors"
                        onClick={() => openInvestorDetails(investor.id)}
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      {/* edit button */}
                      <button
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary hover:text-white dark:text-primary-dark hover:bg-primary dark:hover:bg-primary-dark/20 rounded-md transition-colors"
                        onClick={() => console.log("Edit investor:", investor.id)}
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-primary hover:text-white dark:text-primary-dark hover:bg-primary dark:hover:bg-primary-dark/20 rounded-md transition-colors"
                        onClick={() => navigate(`/admin/investors/${investor.id}/transfer`)}
                      >
                        <ArrowLeftRight className="w-4 h-4" />
                      </button>
                      
                      <ActionDropdown
                        onView={() => openInvestorDetails(investor.id)}
                        onEdit={() => console.log("Edit investor:", investor.id)}
                        onToggleStatus={() => console.log("Toggle status:", investor.id)}
                        onTransfer={() => navigate(`/admin/investors/${investor.id}/transfer`)}
                      />
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        <Panigation
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      </div>
    </div>
  );
};

export default InvestorManagement;
